'use client';

import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { User, UserLocation } from '@/types';
import { useToast } from '@/lib/toast';

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
}

const SOCIAL_FIELDS: { key: string; label: string; placeholder: string }[] = [
  { key: 'twitter',   label: 'Twitter / X',  placeholder: 'https://twitter.com/username' },
  { key: 'github',    label: 'GitHub',       placeholder: 'https://github.com/username' },
  { key: 'linkedin',  label: 'LinkedIn',     placeholder: 'https://linkedin.com/in/username' },
  { key: 'instagram', label: 'Instagram',    placeholder: 'https://instagram.com/username' },
  { key: 'website',   label: 'Website',      placeholder: 'https://yourdomain.com' },
  { key: 'youtube',   label: 'YouTube',      placeholder: 'https://youtube.com/@channel' },
];

export default function EditProfileDialog({
  user, onClose, onSaved,
}: {
  user: User;
  onClose: () => void;
  onSaved: (updated: User) => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(user.name);
  const [avatarUrl, setAvatarUrl] = useState(user.avatar_url ?? '');
  const [coverUrl, setCoverUrl] = useState(user.cover_image_url ?? '');
  const [bio, setBio] = useState(user.bio ?? '');
  const [social, setSocial] = useState<Record<string, string>>({ ...(user.social_links ?? {}) });
  const [location, setLocation] = useState<UserLocation | null>(user.location ?? null);
  const [locationDirty, setLocationDirty] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');
  const [locationResults, setLocationResults] = useState<NominatimResult[]>([]);
  const [locationSearching, setLocationSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  // Debounced Nominatim search. Free public API; no key needed.
  // Per their usage policy we throttle to ≤1 req/sec via the 400ms debounce
  // plus only firing when the query is ≥3 chars.
  useEffect(() => {
    const q = locationQuery.trim();
    if (q.length < 3) {
      setLocationResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      setLocationSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=5`,
          { headers: { Accept: 'application/json' } },
        );
        if (res.ok) {
          const data: NominatimResult[] = await res.json();
          setLocationResults(data);
        }
      } catch { /* ignore */ }
      finally { setLocationSearching(false); }
    }, 400);
    return () => clearTimeout(handle);
  }, [locationQuery]);

  const pickLocation = (r: NominatimResult) => {
    setLocation({
      lat: parseFloat(r.lat),
      lng: parseFloat(r.lon),
      label: r.display_name,
    });
    setLocationDirty(true);
    setLocationQuery('');
    setLocationResults([]);
  };

  const clearLocation = () => {
    setLocation(null);
    setLocationDirty(true);
    setLocationQuery('');
    setLocationResults([]);
  };
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstInputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload: Parameters<typeof api.users.updateMe>[0] = {
        name: name.trim(),
        avatar_url: avatarUrl.trim(),
        cover_image_url: coverUrl.trim(),
        bio: bio.trim(),
        social_links: Object.fromEntries(
          Object.entries(social).map(([k, v]) => [k, (v ?? '').trim()])
        ),
      };
      // Only send `location` when the user actually touched it — omitted means
      // "leave alone", explicit null means "clear", object means "set".
      if (locationDirty) payload.location = location;
      const updated = await api.users.updateMe(payload);
      toast.success('Profile saved');
      onSaved(updated);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Couldn\u2019t save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'color-mix(in oklch, black 40%, transparent)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-raised)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          padding: 28,
          width: 640,
          maxWidth: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{
              fontSize: 11, fontWeight: 600, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 4,
            }}>
              Your profile
            </div>
            <div style={{
              fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 700,
              letterSpacing: '-0.02em', color: 'var(--ink)',
            }}>
              Edit profile
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ color: 'var(--ink-3)', background: 'var(--bg-soft)' }}
            aria-label="Close"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
          </button>
        </div>

        {/* Cover preview */}
        <div style={{
          position: 'relative',
          width: '100%', height: 140,
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--bg-soft)',
          marginBottom: 12,
          border: '1px solid var(--line)',
        }}>
          {coverUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={coverUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              display: 'grid', placeItems: 'center',
              fontSize: 13, color: 'var(--ink-3)',
            }}>
              Cover preview
            </div>
          )}
          {/* Avatar preview overlap */}
          <div style={{
            position: 'absolute', bottom: -28, left: 20,
            width: 72, height: 72, borderRadius: '50%',
            border: '3px solid var(--bg-raised)',
            background: 'linear-gradient(135deg, var(--accent), var(--accent-ink))',
            color: 'white',
            display: 'grid', placeItems: 'center',
            fontFamily: 'var(--font-serif)', fontSize: 26, fontWeight: 700,
            overflow: 'hidden',
          }}>
            {avatarUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              name.slice(0, 1).toUpperCase() || 'P'
            )}
          </div>
        </div>
        <div style={{ height: 32 }} />

        <Field label="Name">
          <input ref={firstInputRef} value={name} onChange={(e) => setName(e.target.value)} className="ps-input" />
        </Field>

        <Field label="Avatar URL" hint="Square image works best. Links from Unsplash, a CDN, or wherever.">
          <input value={avatarUrl} onChange={(e) => setAvatarUrl(e.target.value)} placeholder="https://..." className="ps-input" />
        </Field>

        <Field label="Cover image URL" hint="Banner across the top of your profile. Wide images (>1600px) look cleanest.">
          <input value={coverUrl} onChange={(e) => setCoverUrl(e.target.value)} placeholder="https://..." className="ps-input" />
        </Field>

        <Field label="Bio" hint={`${bio.length}/280`}>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, 280))}
            rows={3}
            placeholder="A short intro — who you are and what prompts you like to share."
            className="ps-input"
            style={{ resize: 'vertical', minHeight: 80, fontFamily: 'var(--font-sans)' }}
          />
        </Field>

        <div style={{
          fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.09em',
          color: 'var(--ink-3)', fontWeight: 500, marginTop: 10, marginBottom: 8,
        }}>
          Location <span style={{ color: 'var(--ink-3)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>· shown on the world map</span>
        </div>
        <div style={{ position: 'relative', marginBottom: 14 }}>
          {location ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px',
              border: '1px solid var(--line)', borderRadius: 'var(--radius-md)',
              background: 'var(--bg-soft)',
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 18, color: 'var(--accent)' }}>place</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {location.label || `${location.lat.toFixed(3)}, ${location.lng.toFixed(3)}`}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
                </div>
              </div>
              <button type="button" onClick={clearLocation} className="ps-btn ps-btn-ghost ps-btn-sm">Clear</button>
            </div>
          ) : (
            <>
              <input
                value={locationQuery}
                onChange={(e) => setLocationQuery(e.target.value)}
                placeholder="Search city or place — e.g. Bangkok"
                className="ps-input"
              />
              {(locationResults.length > 0 || locationSearching) && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  marginTop: 4, zIndex: 10,
                  background: 'var(--bg-raised)',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-lg)',
                  maxHeight: 240, overflowY: 'auto',
                }}>
                  {locationSearching && (
                    <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--ink-3)' }}>Searching…</div>
                  )}
                  {locationResults.map((r) => (
                    <button
                      key={r.place_id}
                      type="button"
                      onClick={() => pickLocation(r)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '10px 12px', fontSize: 13, color: 'var(--ink)',
                        background: 'transparent', border: 0, cursor: 'pointer',
                        borderBottom: '1px solid var(--line)',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-soft)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      {r.display_name}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{
          fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.09em',
          color: 'var(--ink-3)', fontWeight: 500, marginTop: 10, marginBottom: 8,
        }}>
          Social links
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          {SOCIAL_FIELDS.map((f) => (
            <Field key={f.key} label={f.label} compact>
              <input
                value={social[f.key] ?? ''}
                onChange={(e) => setSocial((s) => ({ ...s, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                className="ps-input"
                style={{ fontSize: 13 }}
              />
            </Field>
          ))}
        </div>

        {user.is_suspended && (
          <div style={{
            marginTop: 16, padding: '10px 12px',
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.30)',
            borderRadius: 'var(--radius-md)', fontSize: 12, color: 'var(--ink-2)',
          }}>
            <b style={{ color: '#ef4444' }}>Editing is disabled while your account is suspended.</b>{' '}
            The fields above are read-only — backend will refuse the save with 403.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 22 }}>
          <button onClick={onClose} className="ps-btn ps-btn-ghost ps-btn-sm">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim() || !!user.is_suspended}
            aria-busy={saving || undefined}
            className="ps-btn ps-btn-primary ps-btn-sm"
            title={user.is_suspended ? 'Account suspended' : undefined}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children, compact }: {
  label: string;
  hint?: string;
  children: React.ReactNode;
  compact?: boolean;
}) {
  return (
    <div style={{ marginBottom: compact ? 4 : 14 }}>
      <label style={{
        display: 'block', fontSize: 12, fontWeight: 500,
        color: 'var(--ink-2)', marginBottom: 6, letterSpacing: '0.01em',
      }}>
        {label}
        {hint && <span style={{ float: 'right', color: 'var(--ink-3)', fontWeight: 400 }}>{hint}</span>}
      </label>
      {children}
    </div>
  );
}
